import { Flex, NavLink, Menu, Button } from "@mantine/core";
import { MenuType } from "../../types/menu";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { useMemo, type FC } from "react";
import type { NavMenuOptions, NestedMenuOption } from "../../types/menu";
import { IconChevronDown } from "@tabler/icons-react";
import classes from "./SecondaryNavbar.module.css";

interface NavbarProps {
  menu: NavMenuOptions;
}

/**
 * Remove trailing slash from fullPath
 */
function normalizeFullPath(fullPath: string) {
  return fullPath.replace(/\/$/, "");
}

export const SecondaryNavbar: FC<NavbarProps> = ({ menu }) => {
  const matches = useMatches();
  const navigate = useNavigate();

  const [lastMatch] = matches.slice(-1);

  if (!lastMatch) {
    throw new Error("No match found, this should never happen");
  }

  const currentMenu = useMemo(() => {
    return menu.items
      .filter(item => item.type === MenuType.Nested)
      .find(item => normalizeFullPath(lastMatch.fullPath).startsWith(item.prefix));
  }, [menu, lastMatch]);

  const renderDesktopMenu = (item: NestedMenuOption) => {
    const {
      options,
    } = item;

    return options.map(({ title, Icon, to, params, search }) => {
      const isActive = normalizeFullPath(lastMatch.fullPath) === to;
      return (
        <NavLink
          onClick={() => {
            void navigate({ to, params, search });
          }}
          key={title}
          active={isActive}
          leftSection={Icon ? <Icon /> : undefined}
          label={title}
        />
      );
    });
  };

  const renderMobileMenu = (item: NestedMenuOption) => {
    const {
      options,
    } = item;

    const activeItem = options.find(option => normalizeFullPath(lastMatch.fullPath) === option.to);

    return (
      <Menu shadow="md" width={200} position="bottom-start">
        <Menu.Target>
          <Button
            variant="default"
            rightSection={<IconChevronDown size={16} />}
            fullWidth
            justify="space-between"
          >
            {activeItem?.title}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {options.map(({ Icon, title, to, params, search }) => {
            return (
              <Menu.Item
                key={title}
                leftSection={Icon ? <Icon /> : undefined}
                onClick={() => {
                  void navigate({ to, params, search });
                }}
              >
                {title}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  };

  if (!currentMenu) return null;

  return (
    <>
      <Flex direction="column" className={classes.desktopNav}>
        {renderDesktopMenu(currentMenu)}
      </Flex>

      <Flex direction="row" className={classes.mobileNav}>
        {renderMobileMenu(currentMenu)}
      </Flex>
    </>
  );
};
